import {
  fileUploadLimitError,
  type FileValidationError
} from "src/lib/fileUploadLimit";
import type { TranslationKey } from "src/types/translationKey";

export type { FileValidationError };

export type FileValidationMode = "image" | "pdf" | "any";

function getFileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

// Deliberately not an extension list: which image subtypes exist and decode
// differs by device and browser, so the type the browser reports is trusted and
// anything it can't decode fails in the downscaler instead.
export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

export function isPdfFile(file: File): boolean {
  if (file.type === "application/pdf") {
    return true;
  }
  return file.name.toLowerCase().endsWith(".pdf");
}

const TYPE_CHECK_BY_MODE: Record<
  FileValidationMode,
  { accepts: (file: File) => boolean; errorKey: TranslationKey }
> = {
  image: {
    accepts: isImageFile,
    errorKey: "leapChat.attachment.invalidImageFileType"
  },
  pdf: {
    accepts: isPdfFile,
    errorKey: "leapChat.attachment.invalidPdfFileType"
  },
  any: {
    accepts: (file) => isImageFile(file) || isPdfFile(file),
    errorKey: "leapChat.attachment.invalidFileType"
  }
};

export function validateFile(
  file: File,
  mode: FileValidationMode
): FileValidationError | null {
  const { accepts, errorKey } = TYPE_CHECK_BY_MODE[mode];

  if (!accepts(file)) {
    const extension = getFileExtension(file.name) || file.type || "unknown";
    return { key: errorKey, variables: { extension } };
  }

  return fileUploadLimitError(file);
}
